import { getStaticFallbackSpotById, getStaticFallbackSpots } from "./static-catalog";
import { sharedSupabase } from "./supabase-client";
import { ISLAND_BY_CODE } from "./caribbeanIslands";
import { getIslandSpotsSync } from "./island-activities-api";
import { applyTierToSpots } from "./tier-spots";

const DEFAULT_IMAGE =
  "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80";

const SPOT_IMAGE_BY_CATEGORY = {
  beach: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800&q=80",
  restaurant: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80",
  bar: "https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=800&q=80",
  nightclub: "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=800&q=80",
  adventure: "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=800&q=80",
  attraction: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80",
  shopping: "https://images.unsplash.com/photo-1512436991641-6745cdb1723f?w=800&q=80",
  hotel: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80",
};

const COUNTRY_NAMES = Object.fromEntries(
  Object.entries(ISLAND_BY_CODE).map(([code, island]) => [code, island.name])
);

function normalizeCountry(location = "", preferredCountry = "barbados") {
  const text = String(location).toLowerCase();
  for (const [code, island] of Object.entries(ISLAND_BY_CODE)) {
    if (text.includes(island.name.toLowerCase()) || text.includes(code.replace("_", " "))) {
      return code;
    }
  }
  return preferredCountry;
}

function mergeSpotLists(primary = [], secondary = []) {
  const seen = new Set(primary.map((s) => (s.name || "").toLowerCase()));
  const merged = [...primary];
  for (const spot of secondary) {
    const key = (spot.name || "").toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    merged.push(spot);
  }
  return merged;
}

export async function fetchFallbackSpots({
  country = "barbados",
  category = "",
  search = "",
  limit = 50,
  isPro = false,
  limitReached = false,
} = {}) {
  const preferredCountry = country.toLowerCase();
  const staticFallback = getStaticFallbackSpots({ country: preferredCountry, category, search, limit });
  const islandSpots = getIslandSpotsSync({
    country: preferredCountry,
    category,
    search,
    isPro,
    limitReached,
  }).spots;

  const catalog = mergeSpotLists(staticFallback, islandSpots);

  if (!sharedSupabase) {
    return applyTierToSpots(catalog, { isPro, limitReached }).slice(0, limit);
  }

  let query = sharedSupabase
    .from("businesses")
    .select("id,name,description,category,location")
    .eq("site_origin", "caribnavi.com")
    .order("created_at", { ascending: false })
    .limit(Math.max(limit, 12));

  if (category) {
    query = query.eq("category", category);
  }

  if (search) {
    const escaped = search.replace(/,/g, " ").trim();
    if (escaped) {
      query = query.or(`name.ilike.%${escaped}%,description.ilike.%${escaped}%`);
    }
  }

  const { data, error } = await query;
  if (error) {
    return applyTierToSpots(catalog, { isPro, limitReached }).slice(0, limit);
  }

  const rows = Array.isArray(data) ? data : [];
  const filtered = rows.filter((row) => normalizeCountry(row.location, preferredCountry) === preferredCountry);

  const mapped = filtered.map((row, index) => {
    const categoryValue = (row.category || "attraction").toLowerCase();
    const inferredCountry = normalizeCountry(row.location, preferredCountry);
    return {
      spot_id: `supa-${row.id || index}`,
      name: row.name,
      description: row.description || row.name,
      short_description: row.description || row.name,
      category: categoryValue,
      country: inferredCountry,
      location: row.location || COUNTRY_NAMES[inferredCountry] || COUNTRY_NAMES[preferredCountry],
      image_url: SPOT_IMAGE_BY_CATEGORY[categoryValue] || DEFAULT_IMAGE,
      images: [SPOT_IMAGE_BY_CATEGORY[categoryValue] || DEFAULT_IMAGE],
      rating: 4.7,
      is_featured: true,
      is_premium_content: false,
      locked: false,
    };
  });

  const merged = mapped.length > 0 ? mergeSpotLists(mapped, catalog) : catalog;
  return applyTierToSpots(merged, { isPro, limitReached }).slice(0, limit);
}

export function buildFallbackRecommendations(spots, country = "barbados") {
  const top = Array.isArray(spots) ? spots.slice(0, 4) : [];
  return {
    title: `Popular picks in ${COUNTRY_NAMES[country] || ISLAND_BY_CODE[country]?.name || "the Caribbean"}`,
    context: top.some((spot) => String(spot.spot_id || "").startsWith("seed-"))
      ? "Connected to Mansion Global Network"
      : "Live CaribNavi catalog",
    spots: top,
  };
}

export function getFallbackSpotById(spotId) {
  return getStaticFallbackSpotById(spotId);
}
